import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";

import { CustomCost } from "../../../components/CustomCost/CustomCost";
import { CustomIconButton } from "../../../components/CustomIconButton/CustomIconButton";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { ContainerCollaborator } from "../../../containers/ContainerCollaborator/ContainerCollaborator";
import { colors } from "../../../styles/colors";
import { InfosCollabDashboard } from "../InfosCollabDashboard/InfosCollabDashboard";
import useStyles from "./styles";
import { useGetTCOByEmployee } from "../../../api/tco";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import { formatEuros } from "../../../utils/money";

type props = {
  /**
   * Required. Was hardcoded to -1 at the call site, so the panel always asked
   * the API for employee -1 and rendered a blank collaborator card.
   */
  userId: number;
  /** Shown while the collaborator card loads, so the panel is never anonymous. */
  employeeName?: string;
  onClose?: () => void;
};

export const ResultSearchCollab: FC<props> = ({
  userId,
  employeeName,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { tcoByEmployee, isLoading } = useGetTCOByEmployee(
    getCurrentFleetId(),
    userId
  );

  // Three distinct outcomes, not two. The API answers 0/0 rather than erroring
  // for an employee with no vehicle, so the figures alone cannot tell apart
  // "drives nothing" from "drives cars that are between contracts" -- and the
  // vehicle list rendered just above would flatly contradict the wrong one.
  const assignedCount = tcoByEmployee?.assigned_vehicle_count ?? 0;
  const costedCount = tcoByEmployee?.vehicle_count ?? 0;

  const hasNoVehicle = !isLoading && assignedCount === 0;
  const hasNoActiveContract =
    !isLoading && assignedCount > 0 && costedCount === 0;

  return (
    <div className={classes.divResultSearch}>
      <div className={classes.divTextResult}>
        <CustomTypography
          text={
            employeeName
              ? `${t("common.result", "Result")} — ${employeeName}`
              : t("common.result", "Result")
          }
          variant={"default"}
          fontWeight={"bold"}
        />
        <CustomIconButton
          color={colors.BLACK}
          backgroundColor={colors.default}
          widthIcon={"25px"}
          heightIcon={"25px"}
          iconName="close"
          onClick={onClose}
        />
      </div>

      <ContainerCollaborator userId={userId} />
      <InfosCollabDashboard userId={userId} />

      <div className={classes.divContentTCO}>
        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={90} className={classes.skeletonCard} />
            <Skeleton variant="rounded" height={90} className={classes.skeletonCard} />
          </>
        ) : hasNoVehicle ? (
          <CustomTypography
            text={t(
              "fleet.noVehicleAssigned",
              "No vehicle is currently assigned to this collaborator."
            )}
            variant={"default"}
            color={colors.mutedText}
          />
        ) : hasNoActiveContract ? (
          <CustomTypography
            text={t(
              "fleet.noActiveContractForEmployee",
              "No active contract on this collaborator's vehicles, so there is no cost to report."
            )}
            variant={"default"}
            color={colors.mutedText}
          />
        ) : (
          <>
            <CustomCost
              label={t(
                "panel.average.averageMonthlyCost",
                "AVERAGE MONTHLY COST"
              )}
              value={formatEuros(tcoByEmployee?.total_monthly_cost)}
            />
            <CustomCost
              label={t("panel.average.totalCost", "TOTAL COST")}
              value={formatEuros(tcoByEmployee?.total_tco_cost)}
            />
          </>
        )}
      </div>
    </div>
  );
};
